import { type FunctionComponent } from "preact";
import Spinner from "components/Spinner";
import classNames from "helpers/classNames";
import styles from "./VendorsTab.module.scss";
import { useRef, useState } from "preact/hooks";
import {
  WITH_BG_COLOR_ATTR,
  WITH_OUTLINE_ACCENT_COLOR_ATTR,
  WITH_TABINDEX_ATTR,
} from "constants/commonAttributes";
import useTranslation from "hooks/useTranslation";
import useAppState from "hooks/useAppState";
import Filter, { IFilterElement } from "components/Filter";
import TCFVendorList from "widgets/SettingsModal/components/TCFVendorList";
import TCFACVendorList from "widgets/SettingsModal/components/TCFACVendorList";
import ServiceList from "components/ServiceList";
import { NULL } from "constants/primitives";
import { BORDER_OPACITY } from "styles/bootstrap";
import filterVendors from "./helpers/filterVendors";
import calculateFilterBaseContent from "./helpers/calculateFilterBaseContent";

const cx = classNames(styles);

const VendorsTab: FunctionComponent = () => {
  const { widgetConfig, gvl } = useAppState();
  const { t, textDirection } = useTranslation();
  const [search, setSearch] = useState("");

  const [filteredCategories, setFilteredCategories] = useState(
    calculateFilterBaseContent(widgetConfig, t, gvl)
  );

  const inputRef = useRef<HTMLInputElement | null>(NULL);

  const handleChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    setSearch(target.value);

    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleFilterChange = ({ id, category, isVisible }: IFilterElement) => {
    // Update right element/category based on filter change
    if (category) {
      setFilteredCategories([
        ...filterVendors({
          id,
          category,
          shouldBeVisible: !isVisible,
          currentCategories: filteredCategories,
          isTcfEnabled: widgetConfig.tcfEnabled,
        }),
      ]);
    }
  };

  return (
    <>
      {widgetConfig.tcfEnabled ? (
        gvl ? (
          <>
            <div
              class={cx("d-flex", "pb-2", "wrap")}
              data-testid="vendorsTab-filterWrapper"
            >
              <div class={cx("field-wrap", BORDER_OPACITY)}>
                <input
                  ref={inputRef}
                  {...WITH_TABINDEX_ATTR}
                  {...WITH_OUTLINE_ACCENT_COLOR_ATTR}
                  {...WITH_BG_COLOR_ATTR}
                  class={cx("input", "w-100", "p-2", "pl-3", BORDER_OPACITY)}
                  dir={textDirection}
                  type="text"
                  placeholder={t("modal.search")}
                  name="search"
                  value={search}
                  onInput={handleChange}
                />
              </div>
              <Filter
                elements={filteredCategories}
                callback={handleFilterChange}
              />
            </div>
            <div
              class={cx("vendor-list")}
              data-testid="vendorsTab-tcfVendorList"
            >
              {/* Tcf vendors */}
              {widgetConfig.tcfEnabled && (
                <TCFVendorList
                  filterCategories={filteredCategories}
                  searchPhrase={search}
                ></TCFVendorList>
              )}
              {/* Google ac vendors */}
              {widgetConfig.tcfEnabled && widgetConfig.acEnabled && (
                <TCFACVendorList
                  filterCategories={filteredCategories}
                  searchPhrase={search}
                />
              )}
              {/* Normal services */}
              <ServiceList
                filterCategories={filteredCategories}
                searchPhrase={search}
              />
            </div>
          </>
        ) : (
          <div
            data-testid="vendorsTab-spinner"
            class={cx("d-flex", "justify-content-center", "spinnerWrapper")}
          >
            <Spinner />
          </div>
        )
      ) : (
        <ServiceList
          data-testid="vendorsTab-serviceList"
          filterCategories={filteredCategories}
          searchPhrase={search}
        />
      )}
    </>
  );
};

export default VendorsTab;
